import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  IconButton,
  TextField,
  Box,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  ListItem,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Snackbar,
  Skeleton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckIcon from "@mui/icons-material/Check";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import ScrollView from "devextreme-react/scroll-view";
import { useAuth } from "../../contexts/auth";
import { baseAxios } from "../../utils/config";
import "./autocomplete.scss";

export default function AsyncAutocompleteAddClient({
  label = "Label",
  onChange,
  datasets,
  className,
  keyField,
  titleField,
  subtitleField,
  phoneField,
  emailField,
  disabled = false,
  value,
  selectedLocation,
  clientIdFromURL,
  pageCount = 20,
  onClientCreated,
}) {
  const { user } = useAuth();

  const [openList, setOpenList] = useState(false);
  const [clipData, setClipData] = useState([]);
  const [showClear, setShowClear] = useState(false);
  const [text, setText] = useState("");
  const [sortData, setSortData] = useState([]);
  const [selectedData, setSelectedData] = useState(null);
  const [error, setError] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [apiPage, setApiPage] = useState(0);
  const [apiTotalCount, setApiTotalCount] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const listRef = useRef(null);
  const textRef = useRef(null);
  const containerRef = useRef(null);

  const normalizePhoneLikeSearch = (raw) => {
    const value = `${raw ?? ""}`;
    if (/[A-Za-z]/.test(value)) return value;
    if (!/\d/.test(value)) return value;
    return value.replace(/\D/g, "");
  };

  // Modal state
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    birthDate: null,
  });
  const [formErrors, setFormErrors] = useState({});
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Ensure we never show duplicate clients in the autocomplete list view.
  const dedupeByClientId = (items = []) => {
    const map = new Map();
    items.forEach((item) => {
      if (item && item.client_id != null && !map.has(item.client_id)) {
        map.set(item.client_id, item);
      }
    });
    return Array.from(map.values());
  };

  const handleScroll = (e) => {
    const scrollViewInstance = listRef.current?.instance;
    if (!scrollViewInstance) return;
    const reachedBottom =
      scrollViewInstance.scrollHeight() ===
      scrollViewInstance.clientHeight() + e.component.scrollTop();

    if (
      reachedBottom &&
      !searchLoading &&
      !isLoadingMore &&
      sortData.length < apiTotalCount
    ) {
      fetchNextApiPage();
    }
  };

  const handleCreateClient = () => {
    setOpenModal(true);
    setOpenList(false);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      birthDate: null,
    });
    setFormErrors({});
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "This field is required";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "This field is required";
    }

    if (!formData.phone) {
      newErrors.phone = "This field is required";
    } else if (formData.phone.length < 7 || formData.phone.length > 12) {
      newErrors.phone = "Please enter a valid phone number";
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handlePhoneChange = (phone) => {
    setFormData((prev) => ({
      ...prev,
      phone: phone,
    }));
    if (formErrors.phone) {
      setFormErrors((prev) => ({
        ...prev,
        phone: "",
      }));
    }
  };

  const handleDateChange = (date) => {
    setFormData((prev) => ({
      ...prev,
      birthDate: date,
    }));
    if (formErrors.birthDate) {
      setFormErrors((prev) => ({
        ...prev,
        birthDate: "",
      }));
    }
  };

  const handleSubmitClient = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    const { firstName, lastName, email, phone, birthDate } = formData;
    const organization = user.org;
    const location =
      selectedLocation?.location_id !== -1
        ? selectedLocation?.location_id
        : user.location_id;

    let date = null;
    if (birthDate && dayjs(birthDate).isValid()) {
      date = "1904/" + dayjs(birthDate).format("MM/DD");
    }

    try {
      const response = await baseAxios.post("gateway/z1gki3w695", {
        firstName,
        lastName,
        email,
        location,
        phone,
        organization,
        date,
      });

      const responseData = response.data;
      const clientId = responseData.client_id;

      if (response.status === 200) {
        setNotification({
          open: true,
          message: "Client created successfully",
          severity: "success",
        });

        // Build a normalized client object compatible with scheduler datasets
        const newClient = {
          client_id: clientId,
          client_name: `${firstName} ${lastName}`.trim(),
          client_organization_id: organization,
          client_mobile_phone_number: phone,
          client_email: email,
          location_id: location,
        };

        // Automatically select the newly created client in the autocomplete
        setSelectedData(newClient);
        setText(newClient[titleField] || "");
        setShowClear(true);
        setError(false);
        if (onChange) {
          onChange(newClient);
        }

        handleCloseModal();

        // Optional callback for parent components that need to react
        if (onClientCreated) {
          onClientCreated(newClient);
        }
      } else {
        setNotification({
          open: true,
          message: responseData.message,
          severity: "error",
        });
      }
    } catch (error) {
      setNotification({
        open: true,
        message: error.response?.data?.message || "An error occurred",
        severity: "error",
      });
    }
    setLoading(false);
  };

  const handleBlur = (e) => {
    const relatedTarget = e.relatedTarget || document.activeElement;
    const autocompleteList = listRef.current?.instance.element();

    if (autocompleteList && autocompleteList.contains(relatedTarget)) {
      return;
    }

    checkValidation();
  };

  const handleChange = (e, data) => {
    if (data) {
      setText(data[titleField]);
      setSelectedData(data);
      onChange(data);
      setShowClear(true);
      setTimeout(() => {
        setOpenList(false);
      }, 100);
    } else {
      const enteredValue = normalizePhoneLikeSearch(e.target.value);
      setText(enteredValue);
      setShowClear(enteredValue !== "");
      setSelectedData(null);
      // Search is handled by debounced API request (no client-side filtering).
    }
  };

  const refreshData = () => {
    // List is always loaded from backend; no in-memory refresh.
  };

  const resetList = () => {
    setOpenList(false);
    setError(false);
  };

  const checkValidation = () => {
    if (selectedData) {
      setError(false);
      setOpenList(false);
    } else {
      resetList();
      setError(true);
    }
  };

  const handleListItemClick = (data) => {
    handleChange({ target: { value: data[titleField] } }, data);
  };

  useEffect(() => {
    setClipData(dedupeByClientId(sortData));
  }, [sortData]);

  // Debounce search text for server request
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(text);
    }, 500);
    return () => clearTimeout(t);
  }, [text]);

  // Fetch clients from backend when debounced search or location changes
  useEffect(() => {
    setSearchLoading(true);
    setSortData([]);
    setClipData([]);
    setApiPage(0);
    setApiTotalCount(0);

    const fetchClients = async () => {
      try {
        const search = debouncedSearch.trim();
        const params = {
          page: 0,
          limit: pageCount,
          sortBy: "client_last_name",
          sortOrder: "asc",
        };
        if (
          selectedLocation &&
          selectedLocation.location_id !== undefined &&
          selectedLocation.location_id !== -1
        ) {
          params.location_id = selectedLocation.location_id;
        }
        if (search) params.search = search;

        const res = await baseAxios.get("/clients/list", { params });
        const items = res?.data?.Items || [];
        const total = res?.data?.totalCount ?? items.length;

        const normalized = items.map((item) => ({
          client_id: item.client_id,
          client_name: `${item.client_first_name ?? ""} ${
            item.client_last_name ?? ""
          }`.trim(),
          client_mobile_phone_number: item.client_mobile_phone_number,
          client_email: item.client_email,
        }));

        setSortData(normalized);
        setApiPage(0);
        setApiTotalCount(total);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error searching clients:", err);
      } finally {
        setSearchLoading(false);
      }
    };

    fetchClients();
  }, [debouncedSearch, pageCount, selectedLocation]);

  const fetchNextApiPage = async () => {
    const search = debouncedSearch.trim();
    const nextPage = apiPage + 1;

    if (sortData.length >= apiTotalCount) return;

    try {
      setIsLoadingMore(true);
      setSearchLoading(true);
      const params = {
        page: nextPage,
        limit: pageCount,
        sortBy: "client_last_name",
        sortOrder: "asc",
      };
      if (
        selectedLocation &&
        selectedLocation.location_id !== undefined &&
        selectedLocation.location_id !== -1
      ) {
        params.location_id = selectedLocation.location_id;
      }
      if (search) params.search = search;

      const res = await baseAxios.get("/clients/list", { params });
      const items = res?.data?.Items || [];

      const normalized = items.map((item) => ({
        client_id: item.client_id,
        client_name: `${item.client_first_name ?? ""} ${
          item.client_last_name ?? ""
        }`.trim(),
        client_mobile_phone_number: item.client_mobile_phone_number,
        client_email: item.client_email,
      }));

      setSortData((prev) => {
        const existingIds = new Set(prev.map((c) => c.client_id));
        const uniqueToAppend = normalized.filter(
          (c) => !existingIds.has(c.client_id)
        );
        return [...prev, ...uniqueToAppend];
      });
      setApiPage(nextPage);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error loading more clients:", err);
    } finally {
      setSearchLoading(false);
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    if (value && value[titleField]) {
      setSelectedData(value);
      setText(value[titleField]);
      setShowClear(true);
    } else {
      setSelectedData(null);
      setText("");
      setShowClear(false);
    }
  }, [value]);

  useEffect(() => {
    if (clientIdFromURL) {
      const clientObject = datasets.find(
        (client) => client[keyField].toString() === clientIdFromURL
      );
      if (clientObject) {
        setSelectedData(clientObject);
        setText(clientObject[titleField]);
        setShowClear(true);
      }
    }
  }, []);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div
        ref={containerRef}
        className={`async-autocomplete-container ${className} ${
          disabled ? "disabled-box" : ""
        }`}
      >
        {error && !selectedData && (
          <Typography className="error" variant="body2">
            Please select one of {label}.
          </Typography>
        )}
        <TextField
          className={`autocomplete-textfield ${selectedData ? "success" : ""}`}
          size="small"
          variant="outlined"
          label={label}
          value={text}
          error={error && !selectedData}
          inputRef={textRef}
          onFocus={() => setOpenList(true)}
          onBlur={handleBlur}
          disabled={disabled}
          onChange={handleChange}
          onPaste={(e) => {
            const pasted = e.clipboardData?.getData?.("text") ?? "";
            const normalized = normalizePhoneLikeSearch(pasted);
            if (normalized !== pasted) {
              e.preventDefault();
              handleChange({ target: { value: normalized } }, null);
            }
          }}
        />
        <Box className="button-box">
          {showClear && (
            <IconButton
              className="iconbtn"
              size="small"
              aria-label="close"
              disabled={disabled}
              onClick={() => {
                setText("");
                setShowClear(false);
                resetList();
                refreshData();
                setSelectedData(null);
                onChange(null);
                textRef.current.focus();
              }}
            >
              <CloseIcon className="icon" />
            </IconButton>
          )}
          <IconButton
            className="iconbtn"
            size="small"
            aria-label="open"
            disabled={disabled}
            onClick={() => {
              setOpenList(!openList);
            }}
          >
            <KeyboardArrowDownIcon className="icon" />
          </IconButton>
        </Box>
        {openList && (
          <Box
            className="scheduler-dropdown"
            sx={{
              position: "fixed",
              width: containerRef.current?.offsetWidth || 300,
              zIndex: 9999,
              bgcolor: "background.paper",
              boxShadow: 4,
              borderRadius: 1,
              border: "1px solid #bbbbbb",
            }}
            style={{
              top: containerRef.current
                ? containerRef.current.getBoundingClientRect().bottom + 2
                : 0,
              left: containerRef.current
                ? containerRef.current.getBoundingClientRect().left
                : 0,
            }}
          >
            <ScrollView
              ref={listRef}
              className="autocomplete-list-fixed"
              height={220}
              onScroll={handleScroll}
            >
              <List component="nav">
                {/* Skeletons while loading initial results */}
                {searchLoading &&
                  clipData.length === 0 &&
                  Array.from({ length: 10 }).map((_, idx) => (
                    <ListItem key={`skeleton-${idx}`} className="list-item">
                      <ListItemText
                        className="item-text"
                        primary={<Skeleton width="60%" />}
                        secondary={<Skeleton width="50%" />}
                      />
                    </ListItem>
                  ))}

                {clipData.map((data) => {
                  let isSelected = false;
                  if (selectedData) {
                    isSelected = data[keyField] === selectedData[keyField];
                  }
                  return (
                    <ListItemButton
                      key={data[keyField]}
                      className="list-item"
                      selected={isSelected}
                      disabled={disabled}
                      onClick={() => handleListItemClick(data)}
                    >
                      <ListItemText
                        className="item-text"
                        primary={data[titleField] || " "}
                        secondary={
                          <>
                            <div>{data[subtitleField] || ""}</div>
                            <div>
                              {data[emailField] !== null &&
                              data[emailField] !== undefined &&
                              data[emailField] !== "<null>"
                                ? data[emailField]
                                : ""}
                            </div>
                            <div>
                              {data[phoneField] !== null &&
                              data[phoneField] !== undefined &&
                              data[phoneField] !== "<null>"
                                ? data[phoneField]
                                : ""}
                            </div>
                          </>
                        }
                      />
                      {isSelected && <CheckIcon className="icon-check" />}
                    </ListItemButton>
                  );
                })}

                {/* Skeleton row when appending more pages */}
                {searchLoading && clipData.length > 0 && (
                  <ListItem className="list-item">
                    <ListItemText
                      className="item-text"
                      primary={<Skeleton width="40%" />}
                      secondary={<Skeleton width="30%" />}
                    />
                  </ListItem>
                )}
                {/* {clipData.length === 0 && (
              <ListItemButton
                className="list-item"
                onClick={() => textRef.current.focus()}
              >
                <ListItemText
                  className="item-text no-data"
                  primary="No Data"
                  secondary="Please search by other keywords"
                />
              </ListItemButton>
            )} */}
                {!searchLoading && clipData.length === 0 && (
                  <ListItem className="list-item">
                    <ListItemText
                      className="item-text no-data"
                      primary="No Clients Found"
                      secondary="Would you like to add a new client?"
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleCreateClient}
                    >
                      Add Client
                    </Button>
                  </ListItem>
                )}
              </List>
            </ScrollView>
          </Box>
        )}

        {/* Create Client Modal */}
        <Dialog
          open={openModal}
          onClose={handleCloseModal}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h6">Add New Client</Typography>
            <IconButton
              aria-label="close"
              onClick={handleCloseModal}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box component="form" onSubmit={handleSubmitClient}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    First Name <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <TextField
                    variant="outlined"
                    size="small"
                    value={formData.firstName}
                    onChange={handleInputChange("firstName")}
                    error={!!formErrors.firstName}
                    helperText={formErrors.firstName}
                    disabled={loading}
                    fullWidth
                    placeholder="Enter first name"
                  />
                </Box>

                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Last Name <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <TextField
                    variant="outlined"
                    size="small"
                    value={formData.lastName}
                    onChange={handleInputChange("lastName")}
                    error={!!formErrors.lastName}
                    helperText={formErrors.lastName}
                    disabled={loading}
                    fullWidth
                    placeholder="Enter last name"
                  />
                </Box>

                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Birth Date
                  </Typography>
                  <DatePicker
                    value={formData.birthDate}
                    onChange={handleDateChange}
                    views={["month", "day"]}
                    format="MM/DD"
                    slotProps={{
                      textField: {
                        variant: "outlined",
                        size: "small",
                        error: !!formErrors.birthDate,
                        helperText: formErrors.birthDate,
                        disabled: loading,
                        fullWidth: true,
                      },
                    }}
                  />
                </Box>

                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Email
                  </Typography>
                  <TextField
                    type="email"
                    variant="outlined"
                    size="small"
                    value={formData.email}
                    onChange={handleInputChange("email")}
                    error={!!formErrors.email}
                    helperText={formErrors.email}
                    disabled={loading}
                    fullWidth
                    placeholder="Enter email address"
                  />
                </Box>

                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Phone Number <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <PhoneInput
                    country={"us"}
                    onlyCountries={["us", "ca"]}
                    placeholder="+1 (701) 234-5678"
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    disabled={loading}
                    inputStyle={{
                      width: "100%",
                      height: "40px",
                      border: formErrors.phone
                        ? "2px solid #d32f2f"
                        : "1px solid rgba(0, 0, 0, 0.23)",
                      borderRadius: "4px",
                      fontSize: "15px",
                      outline: "none",
                    }}
                    buttonStyle={{
                      height: "40px",
                    }}
                    containerStyle={{
                      width: "100%",
                    }}
                  />
                  {formErrors.phone && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5, ml: 2 }}
                    >
                      {formErrors.phone}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseModal} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmitClient}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Create Client"
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Notification Snackbar */}
        <Snackbar
          open={notification.open}
          autoHideDuration={6000}
          onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() =>
              setNotification((prev) => ({ ...prev, open: false }))
            }
            severity={notification.severity}
            sx={{ width: "100%" }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </div>
    </LocalizationProvider>
  );
}
